import React from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
interface WithChildren {
  children: React.ReactNode;
  className?: string;
}
export function Container({
  children,
  className = ''
}: WithChildren) {
  return <div className={`mx-auto w-full max-w-[1240px] px-5 sm:px-8 ${className}`} data-id="element-1338" data-mp="1grzpjrwgpioe">{children}</div>;
}
interface SectionProps extends WithChildren {
  tone?: 'white' | 'mist' | 'dark' | 'champagne';
  id?: string;
  'aria-labelledby'?: string;
}
const tones: Record<NonNullable<SectionProps['tone']>, string> = {
  white: 'bg-charcoal',
  mist: 'bg-mist',
  dark: 'surface-dark text-white',
  champagne: 'bg-champagne'
};
export function Section({
  children,
  className = '',
  tone = 'white',
  id,
  ...rest
}: SectionProps) {
  return <section id={id} className={`${tones[tone]} py-16 sm:py-24 ${className}`} {...rest} data-id="element-1339" data-mp="24kg7m2g79anp">
      <Container data-id="element-1340" data-mp="29j9m9y7837pg">{children}</Container>
    </section>;
}
export function Kicker({
  children,
  className = ''
}: WithChildren) {
  return <span className={`inline-block text-[11px] font-semibold uppercase tracking-[0.22em] text-gold ${className}`} data-id="element-1341" data-mp="35kujqciieua3">
      {children}
    </span>;
}
interface SectionHeaderProps {
  kicker?: string;
  title: string;
  lead?: string;
  id?: string;
  align?: 'left' | 'centre';
  reversed?: boolean;
}
export function SectionHeader({
  kicker,
  title,
  lead,
  id,
  align = 'left',
  reversed = false
}: SectionHeaderProps) {
  return <header className={`${align === 'centre' ? 'mx-auto max-w-2xl text-center' : 'max-w-2xl'}`} data-id="element-1342" data-mp="3oaaojc1898wb">
      {kicker && <Kicker data-id="element-1343" data-mp="3jd2annn43r0v">{kicker}</Kicker>}
      <h2 id={id} className={`mt-3 font-display text-[28px] leading-tight sm:text-[36px] ${reversed ? 'text-white' : 'text-ink'}`} data-id="element-1344" data-mp="3elsqh2kc1fwd">
        {title}
      </h2>
      {lead && <p className={`mt-4 text-[17px] ${reversed ? 'text-white/72' : 'text-body'}`} data-id="element-1345" data-mp="ax8y1jvwizp3">{lead}</p>}
    </header>;
}
export function Card({
  children,
  className = ''
}: WithChildren) {
  return <div className={`rounded-md border border-line bg-white p-6 sm:p-7 ${className}`} data-id="element-1346" data-mp="20db7k22e83sv">{children}</div>;
}
export function Pill({
  children,
  className = ''
}: WithChildren) {
  return <span className={`inline-flex items-center rounded-full border border-line bg-white px-3 py-1 text-[11px] font-medium uppercase tracking-[0.12em] text-muted ${className}`} data-id="element-1347" data-mp="1qob3w5cy4k8c">
      {children}
    </span>;
}
export function Callout({
  children,
  className = ''
}: WithChildren) {
  return <div className={`border-l-2 border-gold bg-champagne p-5 text-[14px] text-body ${className}`} data-id="element-1348" data-mp="1mm6an7qetpm8">
      {children}
    </div>;
}
export function PullQuote({
  children,
  className = ''
}: WithChildren) {
  return <blockquote className={`border-l-2 border-gold pl-6 font-display text-[22px] leading-snug text-ink sm:text-[26px] ${className}`} data-id="element-1349" data-mp="1cngvvntt8la7">
      {children}
    </blockquote>;
}
interface RevealProps extends WithChildren {
  delay?: number;
}
export function Reveal({
  children,
  className = '',
  delay = 0
}: RevealProps) {
  const reduced = useReducedMotion();
  if (reduced) return <div className={className} data-id="element-1350" data-mp="qgd3j5x3lwl9">{children}</div>;
  return <motion.div className={className} initial={{
    opacity: 0,
    y: 10
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.4,
    ease: 'easeOut',
    delay
  }} data-id="element-1351" data-mp="qdrxs5toiwi8">
      {children}
    </motion.div>;
}
interface StatProps {
  value: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  label: string;
  source?: string;
  reversed?: boolean;
}
export function StatBlock({
  value,
  prefix = '',
  suffix = '',
  decimals = 0,
  label,
  source,
  reversed = false
}: StatProps) {
  const reduced = useReducedMotion();
  const ref = React.useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const [display, setDisplay] = React.useState(reduced ? value : 0);
  React.useEffect(() => {
    if (!inView || reduced) {
      if (reduced) setDisplay(value);
      return;
    }
    let frame = 0;
    const duration = 1200;
    const start = performance.now();
    const step = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(value * eased);
      if (progress < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [inView, reduced, value]);
  return <div ref={ref} data-id="element-1352" data-mp="38kuro9it7gzu">
      <p className={`font-display text-[40px] leading-none sm:text-[52px] ${reversed ? 'text-white' : 'text-royal'}`} data-id="element-1353" data-mp="38mfh5kq9o9qu">
        {prefix}
        {display.toFixed(decimals)}
        {suffix}
      </p>
      <p className={`mt-3 text-[15px] ${reversed ? 'text-white/75' : 'text-body'}`} data-id="element-1354" data-mp="3ida8h3iddzj5">{label}</p>
      {source && <p className={`mt-2 text-small ${reversed ? 'text-white/45' : 'text-muted'}`} data-id="element-1355" data-mp="2puwifq39zhum">{source}</p>}
    </div>;
}